import { useState } from "react";
import { ChevronLeft, ChevronRight, Shuffle } from "lucide-react";
import { mutate } from "swr";
import { Button } from "~/components/ui/button";
import useToast from "~/components/ui/toast";
import { SettingsSection } from "~/pages/manifold-nextgen/components/settings/SettingsSection";
import {
  AGENT_BRAND_COLORS,
  Agent,
  agentListPath,
  agentPath,
  updateAgent,
} from "~/services/agents";
import {
  AgentAvatar,
  agentAvatarSeed,
  allShapeSeeds,
  resolveAgentAvatarColor,
  resolveShapeVariant,
} from "./AgentAvatar";

const SHAPES_PER_PAGE = 5;

// Which page (of SHAPES_PER_PAGE-sized chunks of `allSeeds`) contains the shape a seed
// resolves to, so the row can open already showing the current shape as selected.
const pageForSeed = (allSeeds: string[], targetSeed: string): number => {
  const targetVariant = resolveShapeVariant(targetSeed);
  const index = allSeeds.findIndex((candidate) => resolveShapeVariant(candidate) === targetVariant);
  return index < 0 ? 0 : Math.floor(index / SHAPES_PER_PAGE);
};

interface AgentAppearanceSectionProps {
  agent: Agent;
}

const initialAvatar = (agent: Agent) => {
  const seed = agentAvatarSeed(agent);
  return { seed, color: resolveAgentAvatarColor(seed, agent.avatar_color) };
};

export const AgentAppearanceSection = ({ agent }: AgentAppearanceSectionProps) => {
  const { showSuccess, showError } = useToast();
  const [isSaving, setIsSaving] = useState(false);

  // The last-saved seed/color, used both as the Reset target and to decide whether the
  // form is dirty. Updated on a successful save so Save/Reset re-disable immediately,
  // without waiting on the parent to re-fetch and pass down a fresh `agent` prop.
  const [committed, setCommitted] = useState(() => initialAvatar(agent));

  const [seed, setSeed] = useState(committed.seed);
  const [color, setColor] = useState(committed.color);

  const isDirty = seed !== committed.seed || color !== committed.color;

  const shapeSeeds = allShapeSeeds();
  const pageCount = Math.max(1, Math.ceil(shapeSeeds.length / SHAPES_PER_PAGE));

  // Opens on whichever page contains the shape already in effect, so it shows as selected
  // like the Color swatches above it do.
  const [page, setPage] = useState(() => pageForSeed(shapeSeeds, seed));
  const pageSeeds = shapeSeeds.slice(
    page * SHAPES_PER_PAGE,
    page * SHAPES_PER_PAGE + SHAPES_PER_PAGE
  );
  const currentShapeVariant = resolveShapeVariant(seed);

  const handleRandomize = () => {
    const newSeed = shapeSeeds[Math.floor(Math.random() * shapeSeeds.length)];
    setColor(AGENT_BRAND_COLORS[Math.floor(Math.random() * AGENT_BRAND_COLORS.length)]);
    setSeed(newSeed);
    setPage(pageForSeed(shapeSeeds, newSeed));
  };

  const handlePrevPage = () => setPage((p) => (p - 1 + pageCount) % pageCount);
  const handleNextPage = () => setPage((p) => (p + 1) % pageCount);

  const handleReset = () => {
    setSeed(committed.seed);
    setColor(committed.color);
    setPage(pageForSeed(shapeSeeds, committed.seed));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateAgent(agent.id, { avatar_seed: seed, avatar_color: color });
      setCommitted({ seed, color });
      await mutate(agentPath(agent.id));
      await mutate(agentListPath());
      showSuccess("Avatar saved");
    } catch {
      showError("Failed to save avatar");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <SettingsSection title="Appearance">
      <div className="grid grid-cols-[280px_1fr] gap-8 px-4 py-4">
        <div className="flex flex-col items-center gap-3">
          <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Preview
          </span>
          <AgentAvatar agent={{ avatar_seed: seed, avatar_color: color }} size={96} />
          <Button variant="outline" size="sm" onClick={handleRandomize} className="w-full">
            <Shuffle className="size-4" />
            Randomize
          </Button>
        </div>

        <div className="flex flex-col gap-5">
          <div>
            <p className="mb-2 text-sm font-medium text-foreground">Color</p>
            <div className="flex flex-wrap gap-2.5">
              {AGENT_BRAND_COLORS.map((swatch) => (
                <button
                  key={swatch}
                  type="button"
                  aria-label={`Use color ${swatch}`}
                  aria-pressed={color === swatch}
                  onClick={() => setColor(swatch)}
                  className="size-7 cursor-pointer rounded-full"
                  style={{
                    backgroundColor: swatch,
                    boxShadow:
                      color === swatch
                        ? `0 0 0 2px var(--background), 0 0 0 4px ${swatch}`
                        : "0 0 0 1px var(--border)",
                  }}
                />
              ))}
            </div>
          </div>

          <div>
            <p className="mb-2 text-sm font-medium text-foreground">Shape</p>
            <div className="flex items-center gap-2.5">
              <Button
                variant="outline"
                size="icon"
                onClick={handlePrevPage}
                className="size-10 shrink-0 rounded-full"
                aria-label={`Show previous shapes (page ${page + 1} of ${pageCount})`}
              >
                <ChevronLeft className="size-4" />
              </Button>
              {pageSeeds.map((sampleSeed) => {
                const isSelected = currentShapeVariant === resolveShapeVariant(sampleSeed);
                return (
                  <button
                    key={sampleSeed}
                    type="button"
                    aria-label="Choose glyph variation"
                    aria-pressed={isSelected}
                    onClick={() => setSeed(sampleSeed)}
                    className="size-10 shrink-0 cursor-pointer rounded-full p-0"
                    style={{
                      boxShadow: isSelected
                        ? `0 0 0 2px var(--background), 0 0 0 4px ${color}`
                        : "0 0 0 1px var(--border)",
                    }}
                  >
                    <AgentAvatar
                      agent={{ avatar_seed: sampleSeed, avatar_color: color }}
                      size={40}
                    />
                  </button>
                );
              })}
              <Button
                variant="outline"
                size="icon"
                onClick={handleNextPage}
                className="size-10 shrink-0 rounded-full"
                aria-label={`Show next shapes (page ${page + 1} of ${pageCount})`}
              >
                <ChevronRight className="size-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 px-4 py-3">
        <Button variant="ghost" onClick={handleReset} disabled={!isDirty || isSaving}>
          Reset
        </Button>
        <Button onClick={handleSave} disabled={!isDirty || isSaving} loading={isSaving}>
          Save
        </Button>
      </div>
    </SettingsSection>
  );
};
